/// <reference types="./Server.d.ts" />
import { EventType, NoOpLogger, notOk, ok, SchemaWithMigrations, } from '@dossierhq/core';
import { authCreateSession, authCreateSyncSessionForSubject, verifyAuthKeysFormat, } from './Auth.js';
import { autGetPrincipals } from './auth/authGetPrincipals.js';
import { autGetPrincipalsTotalCount } from './auth/authGetPrincipalsTotalCount.js';
import { DefaultAuthorizationAdapter } from './AuthorizationAdapter.js';
import { InternalContextImpl, SessionContextImpl, } from './Context.js';
import { managementApplyAuthSyncEvent, managementApplySyncEvent, } from './management/managementApplySyncEvent.js';
import { managementDirtyProcessNextEntity } from './management/managementDirtyProcessNextEntity.js';
import { managementGetSyncEvents, } from './management/managementGetSyncEvents.js';
import { schemaGetSpecification } from './schema/schemaGetSpecification.js';
import { createServerDossierClient } from './ServerDossierClient.js';
import { createServerPublishedClient } from './ServerPublishedClient.js';
import { assertIsDefined } from './utils/AssertUtils.js';
export class ServerImpl {
    #databaseAdapter;
    #logger;
    #adminSchema = null;
    #publishedSchema = null;
    #plugins = [];
    constructor({ databaseAdapter, logger }) {
        this.#databaseAdapter = databaseAdapter;
        this.#logger = logger ?? NoOpLogger;
    }
    async shutdown() {
        if (this.#databaseAdapter) {
            for (const plugin of this.#plugins) {
                plugin.onServerShutdown();
            }
            this.#plugins = [];
            this.#logger.info('Shutting down database adapter');
            await this.#databaseAdapter.disconnect();
            this.#databaseAdapter = null;
            this.#logger.info('Finished shutting server');
            return ok(undefined);
        }
        return notOk.Generic('Trying to shutdown twice');
    }
    async reloadSchema(context) {
        assertIsDefined(this.#databaseAdapter);
        const previousVersion = this.#adminSchema?.spec.version ?? -1;
        const result = await schemaGetSpecification(this.#databaseAdapter, context, true);
        if (result.isError())
            return result;
        this.setAdminSchema(result.value);
        return ok(result.value.version !== previousVersion);
    }
    addPlugin(plugin) {
        this.#plugins.push(plugin);
    }
    getAdminSchema() {
        if (!this.#adminSchema) {
            throw new Error('Schema is not set');
        }
        return this.#adminSchema;
    }
    getPublishedSchema() {
        if (!this.#publishedSchema) {
            throw new Error('PublishedSchema is not set');
        }
        return this.#publishedSchema;
    }
    setAdminSchema(schemaSpec) {
        this.#adminSchema = new SchemaWithMigrations(schemaSpec);
        this.#publishedSchema = this.#adminSchema.toPublishedSchema();
    }
    createInternalContext(databasePerformance) {
        assertIsDefined(this.#databaseAdapter);
        return new InternalContextImpl(this.#databaseAdapter, this.#logger, databasePerformance);
    }
    createSessionContext(session, defaultAuthKeys, logger, databasePerformance) {
        assertIsDefined(this.#databaseAdapter);
        if (!defaultAuthKeys) {
            defaultAuthKeys = [''];
        }
        const verifyResult = verifyAuthKeysFormat(defaultAuthKeys);
        if (verifyResult.isError())
            return verifyResult;
        return ok(new SessionContextImpl(session, defaultAuthKeys, this.#databaseAdapter, logger ?? this.#logger, databasePerformance));
    }
    resolveDossierClientMiddleware(middleware) {
        for (const plugin of this.#plugins) {
            middleware = plugin.onCreateDossierClient(middleware);
        }
        return middleware;
    }
    resolvePublishedClientMiddleware(middleware) {
        for (const plugin of this.#plugins) {
            middleware = plugin.onCreatePublishedDossierClient(middleware);
        }
        return middleware;
    }
}
export async function createServer({ databaseAdapter, authorizationAdapter, logger: serverLogger, }) {
    const serverImpl = new ServerImpl({
        databaseAdapter,
        logger: serverLogger,
    });
    const loadSchemaResult = await serverImpl.reloadSchema(serverImpl.createInternalContext(null));
    if (loadSchemaResult.isError())
        return loadSchemaResult;
    const resolvedAuthorizationAdapter = authorizationAdapter ?? DefaultAuthorizationAdapter;
    const server = {
        shutdown() {
            return serverImpl.shutdown();
        },
        reloadSchema() {
            const managementContext = serverImpl.createInternalContext(null);
            return serverImpl.reloadSchema(managementContext);
        },
        addPlugin(plugin) {
            serverImpl.addPlugin(plugin);
        },
        optimizeDatabase(options) {
            const managementContext = serverImpl.createInternalContext(null);
            return databaseAdapter.managementOptimize(managementContext, options);
        },
        processNextDirtyEntity(filter) {
            const managementContext = serverImpl.createInternalContext(null);
            return managementDirtyProcessNextEntity(serverImpl.getAdminSchema(), databaseAdapter, managementContext, filter);
        },
        getSyncEvents(query) {
            const managementContext = serverImpl.createInternalContext(null);
            return managementGetSyncEvents(databaseAdapter, managementContext, query);
        },
        async applySyncEvent(event) {
            const authContext = serverImpl.createInternalContext(null);
            if (event.type === EventType.createPrincipal) {
                const applyResult = await managementApplyAuthSyncEvent(databaseAdapter, authContext, event);
                if (applyResult.isError())
                    return applyResult;
                return ok(undefined);
            }
            const sessionResult = await authCreateSyncSessionForSubject(databaseAdapter, authContext, {
                subjectId: event.createdBy,
            });
            if (sessionResult.isError())
                return sessionResult;
            const contextResult = serverImpl.createSessionContext(sessionResult.value, null, serverLogger ?? null, null);
            if (contextResult.isError())
                return contextResult;
            const applyResult = await managementApplySyncEvent(serverImpl.getAdminSchema(), resolvedAuthorizationAdapter, databaseAdapter, contextResult.value, event);
            if (applyResult.isError())
                return applyResult;
            if (event.type === EventType.updateSchema) {
                const updatedSchema = applyResult.value;
                serverImpl.setAdminSchema(updatedSchema.spec);
            }
            return ok(undefined);
        },
        getPrincipals(paging) {
            const managementContext = serverImpl.createInternalContext(null);
            return autGetPrincipals(databaseAdapter, managementContext, paging);
        },
        getPrincipalsTotalCount() {
            const managementContext = serverImpl.createInternalContext(null);
            return autGetPrincipalsTotalCount(databaseAdapter, managementContext);
        },
        createSession: async ({ provider, identifier, defaultAuthKeys, logger: sessionLogger, databasePerformance, readonly, }) => {
            const authContext = serverImpl.createInternalContext(databasePerformance ?? null);
            const sessionResult = await authCreateSession(databaseAdapter, authContext, provider, identifier, readonly ?? false);
            if (sessionResult.isError())
                return sessionResult;
            const { principalEffect, session } = sessionResult.value;
            const contextResult = serverImpl.createSessionContext(session, defaultAuthKeys ?? null, sessionLogger ?? serverLogger ?? null, databasePerformance ?? null);
            if (contextResult.isError())
                return contextResult;
            return ok({
                principalEffect,
                context: contextResult.value,
            });
        },
        createDossierClient: (context, middleware) => createServerDossierClient({
            context,
            authorizationAdapter: resolvedAuthorizationAdapter,
            databaseAdapter,
            serverImpl,
            middleware: serverImpl.resolveDossierClientMiddleware(middleware ?? []),
        }),
        createPublishedDossierClient: (context, middleware) => createServerPublishedClient({
            context,
            authorizationAdapter: resolvedAuthorizationAdapter,
            databaseAdapter,
            serverImpl,
            middleware: serverImpl.resolvePublishedClientMiddleware(middleware ?? []),
        }),
    };
    return ok(server);
}
//# sourceMappingURL=Server.js.map